import React from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import useTranslation from 'shared/hooks/useTranslation';
import Description from './description';
import { BadgeProps, BADGES_LANGUAGES } from '../badgesData';
import HeaderSection from './Header';

const Wrapper = styled.div<WrapperProps>`
  display: grid;
  position: relative;
  grid-template-columns: 1fr;
  height: ${({ withHeader }) => (withHeader ? 'calc(100% - 20vh)' : '100%')};
  margin-bottom: 5.4rem;

  ${({ theme }) => theme.MEDIA.MD} {
    grid-template-columns: 6fr 7fr;
    opacity: ${({ onTransition }) => (onTransition ? 0 : 1)};
    transition: opacity 0.7s ease;

    &:before {
      content: '';
      height: 100%;
      width: 67%;
      opacity: 0.1;
      z-index: 1;
      background-image: ${({ bgImage }) => `url(${bgImage})` || 'none'};
      background-repeat: no-repeat;
      background-size: cover;
      background-position: top right;
      position: absolute;
      top: 0;
      left: -17%;

      @media (-webkit-min-device-pixel-ratio: 1.25) {
        transform: scale(0.9);
      }
    }
  }
  background: transparent;

  img {
    position: relative;
    z-index: 100;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (-webkit-min-device-pixel-ratio: 1.25) {
    transform: scale(0.8);
  }
`;

const Right = styled.div`
  h2 {
    font-size: 3.2rem;
    margin-bottom: 2.4rem;
  }

  padding-bottom: 3.2rem;
  max-width: 70%;

  ${({ theme }) => theme.MEDIA.MD} {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    margin: 0 16.8rem 0 1.6rem;

    h2 {
      border-bottom: solid 0.4rem;
      font-size: 6.4rem;
      font-weight: 700;
      line-height: 1.1;
      color: white;
      padding-bottom: 1.6rem;
      width: 100%;

      @media (-webkit-min-device-pixel-ratio: 1.25) {
        font-size: 3.4rem;
        margin-bottom: 0.4rem;
      }
    }
  }
`;

type WrapperProps = {
  withHeader: boolean;
  bgImage: string;
  background: string;
  onTransition?: boolean;
};

export default function Badge({ withHeader, badgeName, urlName, background, onTransition }: BadgeProps): JSX.Element {
  const { t, lang } = useTranslation('badgesPage');

  return (
    <>
      {withHeader ? <HeaderSection /> : null}
      <Wrapper
        withHeader={withHeader}
        bgImage={`/img/badges/v2/${BADGES_LANGUAGES.includes(lang) ? lang : 'en'}/${urlName}.svg`}
        background={background}
        onTransition={onTransition}
      >
        <Left>
          <Image
            src={`/img/badges/v2/${BADGES_LANGUAGES.includes(lang) ? lang : 'en'}/${urlName}.svg`}
            alt={urlName}
            height={500}
            width={500}
          />
        </Left>
        <Right>
          <h2>{t(`badges.${badgeName}`)}</h2>
          <Description badgeName={badgeName} />
        </Right>
      </Wrapper>
    </>
  );
}
